import {
  PUBLIC_CREDENTIAL_DEFAULT_LIMITS,
  type PublicCredentialActionRisk,
  type PublicCredentialPolicyLimits,
} from "@mph/custom-http";

export const LAUNCH_CATALOG_VERSION = "2026-07-19.1" as const;
export const MAX_SOLO_BRANDED_SITES = 20;

export type LaunchCatalogVersion = typeof LAUNCH_CATALOG_VERSION;
export type PlanKey = "free" | "solo" | "team";
export type BillingCadence = "monthly" | "annual";
export type WorkspaceRole = "owner" | "creator" | "viewer";
export type MemberStatus = "active" | "revoked";
export type AccessState = "pending" | "active" | "grace" | "restricted" | "ended";

export const LAUNCH_CONTROL_REASONS = Object.freeze({
  allowed: "ALLOWED",
  authenticationRequired: "AUTHENTICATION_REQUIRED",
  membershipInactive: "MEMBERSHIP_INACTIVE",
  workspaceAccessDenied: "WORKSPACE_ACCESS_DENIED",
  targetNotFound: "TARGET_NOT_FOUND",
  rolePermissionDenied: "ROLE_PERMISSION_DENIED",
  accessStateRestricted: "ACCESS_STATE_RESTRICTED",
  entitlementNotIncluded: "ENTITLEMENT_NOT_INCLUDED",
  entitlementLimitReached: "ENTITLEMENT_LIMIT_REACHED",
  unknownCatalogVersion: "UNKNOWN_CATALOG_VERSION",
  unknownPlan: "UNKNOWN_PLAN",
  providerUnavailable: "BILLING_PROVIDER_UNAVAILABLE",
  validationFailed: "VALIDATION_FAILED",
  conflict: "CONFLICT",
} as const);

export type LaunchControlReasonCode = typeof LAUNCH_CONTROL_REASONS[keyof typeof LAUNCH_CONTROL_REASONS];

export type DomainResult<T, R extends LaunchControlReasonCode = LaunchControlReasonCode> =
  | { ok: true; value: T }
  | { ok: false; reason: R };

export interface PlanPrice {
  readonly monthlyCents: number;
  readonly annualCents: number;
  readonly currency: "USD";
}

export interface PlanEntitlements {
  readonly liveApps: number;
  readonly creatorSeats: number;
  readonly appViewers: "public" | "public-or-password" | "approved-unlimited";
  readonly doorwaySubdomains: number;
  readonly customDomains: number;
  readonly sourceBuilds: boolean;
  readonly sourceSync: boolean;
  readonly managedRuntimes: boolean;
  readonly customApis: boolean;
  readonly publicCredentialApps: boolean;
  readonly managedAi: boolean;
  readonly agentPublishing: boolean;
  readonly teamSkills: boolean;
  readonly hostedCollections: boolean;
  readonly maxAppSizeBytes: number;
  readonly monthlyAppVisits: number;
  readonly monthlyAiRuns: number;
  readonly providerKeyOwner: "creator" | "workspace";
  readonly visibility: "public" | "public-or-password" | "workspace-private-default";
  readonly branding: "required" | "removable";
  readonly revisionHistoryDays: number;
  readonly usageHistoryDays: number;
  readonly ownership: "personal" | "shared-workspace";
  readonly rolesAndOffboarding: boolean;
  readonly identity: "creator-account" | "google-workspace-and-membership";
  readonly auditHistory: boolean;
  readonly support: "self-serve" | "email" | "priority-email";
}

export interface PlanDefinition {
  readonly key: PlanKey;
  readonly name: "Free" | "Solo" | "Team";
  readonly prices: PlanPrice;
  readonly entitlements: PlanEntitlements;
}

export interface PlanCatalog {
  readonly version: LaunchCatalogVersion;
  readonly plans: Readonly<Record<PlanKey, PlanDefinition>>;
}

const MIB = 1024 * 1024;

export const LAUNCH_PLAN_CATALOG: PlanCatalog = Object.freeze({
  version: LAUNCH_CATALOG_VERSION,
  plans: Object.freeze({
    free: plan({
      key: "free",
      name: "Free",
      monthlyCents: 0,
      annualCents: 0,
      liveApps: 1,
      creatorSeats: 1,
      appViewers: "public-or-password",
      doorwaySubdomains: 1,
      customDomains: 0,
      sourceBuilds: false,
      sourceSync: false,
      managedRuntimes: false,
      customApis: false,
      publicCredentialApps: false,
      managedAi: true,
      agentPublishing: false,
      teamSkills: false,
      hostedCollections: true,
      maxAppSizeBytes: 25 * MIB,
      monthlyAppVisits: 2_500,
      monthlyAiRuns: 200,
      providerKeyOwner: "creator",
      visibility: "public-or-password",
      branding: "required",
      revisionHistoryDays: 1,
      usageHistoryDays: 1,
      ownership: "personal",
      rolesAndOffboarding: false,
      identity: "creator-account",
      auditHistory: false,
      support: "self-serve",
    }),
    solo: plan({
      key: "solo",
      name: "Solo",
      monthlyCents: 499,
      annualCents: 4_999,
      liveApps: 2,
      creatorSeats: 1,
      appViewers: "public-or-password",
      doorwaySubdomains: 2,
      customDomains: 1,
      sourceBuilds: true,
      sourceSync: true,
      managedRuntimes: true,
      customApis: true,
      publicCredentialApps: true,
      managedAi: true,
      agentPublishing: true,
      teamSkills: false,
      hostedCollections: true,
      maxAppSizeBytes: 100 * MIB,
      monthlyAppVisits: 25_000,
      monthlyAiRuns: 5_000,
      providerKeyOwner: "creator",
      visibility: "public-or-password",
      branding: "removable",
      revisionHistoryDays: 30,
      usageHistoryDays: 30,
      ownership: "personal",
      rolesAndOffboarding: false,
      identity: "creator-account",
      auditHistory: false,
      support: "email",
    }),
    team: plan({
      key: "team",
      name: "Team",
      monthlyCents: 9_900,
      annualCents: 99_000,
      liveApps: 25,
      creatorSeats: 10,
      appViewers: "approved-unlimited",
      doorwaySubdomains: 25,
      customDomains: 25,
      sourceBuilds: true,
      sourceSync: true,
      managedRuntimes: true,
      customApis: true,
      publicCredentialApps: true,
      managedAi: true,
      agentPublishing: true,
      teamSkills: true,
      hostedCollections: true,
      maxAppSizeBytes: 250 * MIB,
      monthlyAppVisits: 100_000,
      monthlyAiRuns: 25_000,
      providerKeyOwner: "workspace",
      visibility: "workspace-private-default",
      branding: "removable",
      revisionHistoryDays: 90,
      usageHistoryDays: 90,
      ownership: "shared-workspace",
      rolesAndOffboarding: true,
      identity: "google-workspace-and-membership",
      auditHistory: true,
      support: "priority-email",
    }),
  }),
});

interface PlanInput extends PlanEntitlements {
  readonly key: PlanKey;
  readonly name: PlanDefinition["name"];
  readonly monthlyCents: number;
  readonly annualCents: number;
}

function plan(input: PlanInput): PlanDefinition {
  const { key, name, monthlyCents, annualCents, ...entitlements } = input;
  return Object.freeze({
    key,
    name,
    prices: Object.freeze({ monthlyCents, annualCents, currency: "USD" }),
    entitlements: Object.freeze(entitlements),
  });
}

export function getPlanDefinition(
  catalogVersion: string,
  planKey: string,
): DomainResult<PlanDefinition, "UNKNOWN_CATALOG_VERSION" | "UNKNOWN_PLAN"> {
  if (catalogVersion !== LAUNCH_CATALOG_VERSION) {
    return { ok: false, reason: "UNKNOWN_CATALOG_VERSION" };
  }
  if (!isPlanKey(planKey)) return { ok: false, reason: "UNKNOWN_PLAN" };
  return { ok: true, value: LAUNCH_PLAN_CATALOG.plans[planKey] };
}

export function getPlanEntitlementsForBillingQuantity(
  planKey: PlanKey,
  billingQuantity = 1,
): DomainResult<PlanEntitlements, "VALIDATION_FAILED"> {
  if (
    !Number.isInteger(billingQuantity)
    || billingQuantity < 1
    || (planKey === "solo" ? billingQuantity > MAX_SOLO_BRANDED_SITES : billingQuantity !== 1)
  ) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const base = LAUNCH_PLAN_CATALOG.plans[planKey].entitlements;
  if (planKey !== "solo") return { ok: true, value: base };
  const additionalBrandedSites = billingQuantity - 1;
  return {
    ok: true,
    value: Object.freeze({
      ...base,
      liveApps: base.liveApps + additionalBrandedSites,
      doorwaySubdomains: base.doorwaySubdomains + additionalBrandedSites,
      customDomains: base.customDomains + additionalBrandedSites,
    }),
  };
}

function isPlanKey(value: string): value is PlanKey {
  return value === "free" || value === "solo" || value === "team";
}

export type WorkspacePermission =
  | "workspace:view"
  | "shelf:view"
  | "project:view-private"
  | "project:create"
  | "project:manage"
  | "project:publish"
  | "members:view"
  | "members:invite"
  | "members:update-role"
  | "members:remove"
  | "billing:view"
  | "billing:manage"
  | "visibility:make-public"
  | "audit:view"
  | "usage:view"
  | "domain:connect"
  | "skill:view"
  | "skill:publish"
  | "skill:admin";

const VIEWER_PERMISSIONS = [
  "workspace:view",
  "shelf:view",
  "project:view-private",
  "skill:view",
] as const satisfies readonly WorkspacePermission[];

const CREATOR_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  "project:create",
  "project:manage",
  "project:publish",
  "skill:publish",
  "members:view",
  "usage:view",
] as const satisfies readonly WorkspacePermission[];

const OWNER_PERMISSIONS = [
  ...CREATOR_PERMISSIONS,
  "members:invite",
  "members:update-role",
  "members:remove",
  "billing:view",
  "billing:manage",
  "visibility:make-public",
  "audit:view",
  "domain:connect",
  "skill:admin",
] as const satisfies readonly WorkspacePermission[];

export const ROLE_PERMISSIONS: Readonly<Record<WorkspaceRole, ReadonlySet<WorkspacePermission>>> = {
  owner: new Set(OWNER_PERMISSIONS),
  creator: new Set(CREATOR_PERMISSIONS),
  viewer: new Set(VIEWER_PERMISSIONS),
};

const GROWTH_PERMISSIONS = [
  "project:create",
  "members:invite",
  "visibility:make-public",
  "domain:connect",
] as const satisfies readonly WorkspacePermission[];

const ACCESS_RESTRICTED_PERMISSIONS: Readonly<Record<Exclude<AccessState, "active">, ReadonlySet<WorkspacePermission>>> = {
  pending: new Set([...GROWTH_PERMISSIONS, "project:publish", "skill:view", "skill:publish", "skill:admin"]),
  grace: new Set([...GROWTH_PERMISSIONS, "skill:view", "skill:publish", "skill:admin"]),
  restricted: new Set([...GROWTH_PERMISSIONS, "project:publish", "billing:manage", "skill:view", "skill:publish", "skill:admin"]),
  ended: new Set([...GROWTH_PERMISSIONS, "project:publish", "billing:manage", "skill:view", "skill:publish", "skill:admin"]),
};

export type AccessDecision =
  | { ok: true; reason: "ALLOWED" }
  | { ok: false; reason: "ROLE_PERMISSION_DENIED" | "ACCESS_STATE_RESTRICTED" };

export function evaluatePermission(input: {
  role: WorkspaceRole;
  permission: WorkspacePermission;
  access: AccessState;
}): AccessDecision {
  if (!ROLE_PERMISSIONS[input.role].has(input.permission)) {
    return { ok: false, reason: "ROLE_PERMISSION_DENIED" };
  }
  if (
    input.access !== "active"
    && ACCESS_RESTRICTED_PERMISSIONS[input.access].has(input.permission)
  ) {
    return { ok: false, reason: "ACCESS_STATE_RESTRICTED" };
  }
  return { ok: true, reason: "ALLOWED" };
}

export const WORKSPACE_CAPABILITY_KEYS = [
  "sourceBuilds",
  "sourceSync",
  "managedRuntimes",
  "customApis",
  "publicCredentialApps",
  "managedAi",
  "agentPublishing",
  "teamSkills",
  "hostedCollections",
] as const;

export type WorkspaceCapabilityKey = typeof WORKSPACE_CAPABILITY_KEYS[number];
export type WorkspaceCapabilityState =
  | "enabled"
  | "upgrade_required"
  | "permission_denied"
  | "access_paused"
  | "temporarily_unavailable";

export interface WorkspaceCapabilityDecision {
  readonly state: WorkspaceCapabilityState;
}

export type WorkspaceCapabilitySnapshot = Readonly<Record<WorkspaceCapabilityKey, WorkspaceCapabilityDecision>>;
export type WorkspaceCapabilityAvailability = boolean;
export type WorkspaceCapabilityInfrastructure = Readonly<Record<WorkspaceCapabilityKey, WorkspaceCapabilityAvailability>>;

const CAPABILITY_PERMISSION: Readonly<Record<WorkspaceCapabilityKey, WorkspacePermission>> = Object.freeze({
  sourceBuilds: "project:publish",
  sourceSync: "project:publish",
  managedRuntimes: "project:publish",
  customApis: "project:manage",
  publicCredentialApps: "project:manage",
  managedAi: "project:manage",
  agentPublishing: "project:publish",
  teamSkills: "skill:view",
  hostedCollections: "project:manage",
});

export function resolveWorkspaceCapabilities(input: {
  entitlements: PlanEntitlements;
  access: AccessState;
  role: WorkspaceRole;
  memberStatus: MemberStatus;
  infrastructure: WorkspaceCapabilityInfrastructure;
}): WorkspaceCapabilitySnapshot {
  return Object.freeze(Object.fromEntries(WORKSPACE_CAPABILITY_KEYS.map((capability) => [
    capability,
    resolveWorkspaceCapability(input, capability),
  ]))) as WorkspaceCapabilitySnapshot;
}

function resolveWorkspaceCapability(
  input: {
    entitlements: PlanEntitlements;
    access: AccessState;
    role: WorkspaceRole;
    memberStatus: MemberStatus;
    infrastructure: WorkspaceCapabilityInfrastructure;
  },
  capability: WorkspaceCapabilityKey,
): WorkspaceCapabilityDecision {
  if (input.memberStatus !== "active") return { state: "permission_denied" };
  if (capability === "teamSkills" && input.access !== "active") return { state: "access_paused" };
  const permission = evaluatePermission({
    role: input.role,
    permission: CAPABILITY_PERMISSION[capability],
    access: input.access,
  });
  if (!permission.ok) {
    return { state: permission.reason === "ACCESS_STATE_RESTRICTED" ? "access_paused" : "permission_denied" };
  }
  if (!input.entitlements[capability]) return { state: "upgrade_required" };
  return { state: input.infrastructure[capability] ? "enabled" : "temporarily_unavailable" };
}

export function parseWorkspaceCapabilitySnapshot(
  input: unknown,
): DomainResult<WorkspaceCapabilitySnapshot, "VALIDATION_FAILED"> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const value = input as Record<string, unknown>;
  if (Object.keys(value).length !== WORKSPACE_CAPABILITY_KEYS.length) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const snapshot = {} as Record<WorkspaceCapabilityKey, WorkspaceCapabilityDecision>;
  for (const capability of WORKSPACE_CAPABILITY_KEYS) {
    const decision = value[capability];
    if (
      !decision
      || typeof decision !== "object"
      || Array.isArray(decision)
      || Object.keys(decision).length !== 1
      || !isOneOf((decision as Record<string, unknown>).state, [
        "enabled",
        "upgrade_required",
        "permission_denied",
        "access_paused",
        "temporarily_unavailable",
      ])
    ) {
      return { ok: false, reason: "VALIDATION_FAILED" };
    }
    snapshot[capability] = { state: (decision as { state: WorkspaceCapabilityState }).state };
  }
  return { ok: true, value: Object.freeze(snapshot) };
}

export type PublicCredentialLimits = PublicCredentialPolicyLimits;
export type PublicCredentialRisk = PublicCredentialActionRisk;
export const PUBLIC_CREDENTIAL_LIMIT_CEILINGS = PUBLIC_CREDENTIAL_DEFAULT_LIMITS;
export type PublicCredentialPolicyOperation =
  | "enable"
  | "take_offline"
  | "increase_risk"
  | "increase_limits"
  | "lower_limits"
  | "approve_mutation"
  | "reactivate_severe";

export const PUBLIC_CREDENTIAL_NEXT_ACTIONS = Object.freeze({
  askOwner: "ASK_WORKSPACE_OWNER",
  lowerLimits: "LOWER_PUBLIC_API_LIMITS",
  refreshStatus: "REFRESH_PUBLIC_API_STATUS",
  reviewMutation: "REVIEW_EXACT_MUTATION_IDENTITY",
  acknowledgeIncident: "ACKNOWLEDGE_PUBLIC_API_INCIDENT",
  completeRemediation: "COMPLETE_REQUIRED_REMEDIATION",
  waitForCooldown: "WAIT_FOR_SAFETY_COOLDOWN",
} as const);

export type PublicCredentialPolicyDecision =
  | { ok: true; reason: "ALLOWED" }
  | {
      ok: false;
      reason: "OWNER_REQUIRED" | "ROLE_PERMISSION_DENIED" | "PLATFORM_CEILING_EXCEEDED"
        | "VALIDATION_FAILED";
      nextAction: string;
    };

export function evaluatePublicCredentialPolicyChange(input: {
  role: WorkspaceRole;
  operation: PublicCredentialPolicyOperation;
  currentLimits?: Readonly<PublicCredentialLimits>;
  nextLimits?: Readonly<PublicCredentialLimits>;
}): PublicCredentialPolicyDecision {
  if (input.role === "viewer") {
    return {
      ok: false,
      reason: "ROLE_PERMISSION_DENIED",
      nextAction: PUBLIC_CREDENTIAL_NEXT_ACTIONS.askOwner,
    };
  }
  if (
    input.role !== "owner"
    && input.operation !== "take_offline"
  ) {
    return {
      ok: false,
      reason: "OWNER_REQUIRED",
      nextAction: PUBLIC_CREDENTIAL_NEXT_ACTIONS.askOwner,
    };
  }
  if (input.operation === "increase_limits" || input.operation === "lower_limits") {
    if (!input.currentLimits || !input.nextLimits || !validPublicCredentialLimits(input.nextLimits)) {
      return {
        ok: false,
        reason: "VALIDATION_FAILED",
        nextAction: PUBLIC_CREDENTIAL_NEXT_ACTIONS.lowerLimits,
      };
    }
    for (const key of Object.keys(PUBLIC_CREDENTIAL_LIMIT_CEILINGS) as Array<keyof PublicCredentialLimits>) {
      if (input.nextLimits[key] > PUBLIC_CREDENTIAL_LIMIT_CEILINGS[key]) {
        return {
          ok: false,
          reason: "PLATFORM_CEILING_EXCEEDED",
          nextAction: PUBLIC_CREDENTIAL_NEXT_ACTIONS.lowerLimits,
        };
      }
    }
  }
  return { ok: true, reason: "ALLOWED" };
}

function validPublicCredentialLimits(value: Readonly<PublicCredentialLimits>): boolean {
  return (Object.keys(PUBLIC_CREDENTIAL_LIMIT_CEILINGS) as Array<keyof PublicCredentialLimits>)
    .every((key) => Number.isSafeInteger(value[key]) && value[key] >= 0);
}

export function isPublicActionEligible(risk: PublicCredentialRisk): risk is "read" | "mutation" {
  return risk === "read" || risk === "mutation";
}

export interface PublicMutationApprovalIdentity {
  readonly revisionId: string;
  readonly actionVersionId: string;
  readonly destinationDigest: string;
  readonly requestShapeDigest: string;
  readonly connectionGeneration: number;
  readonly credentialVersionId: string;
  readonly policyVersionId: string;
}

export function isCurrentMutationApproval(
  approved: Readonly<PublicMutationApprovalIdentity>,
  current: Readonly<PublicMutationApprovalIdentity>,
): boolean {
  return approved.revisionId === current.revisionId
    && approved.actionVersionId === current.actionVersionId
    && approved.destinationDigest === current.destinationDigest
    && approved.requestShapeDigest === current.requestShapeDigest
    && approved.connectionGeneration === current.connectionGeneration
    && approved.credentialVersionId === current.credentialVersionId
    && approved.policyVersionId === current.policyVersionId;
}

export interface WorkspaceMember {
  readonly id: string;
  readonly role: WorkspaceRole;
  readonly status: MemberStatus;
}

export function countCreatorSeats(members: readonly WorkspaceMember[]): number {
  return members.filter((member) => (
    member.status === "active" && (member.role === "owner" || member.role === "creator")
  )).length;
}

export interface PlanSelection {
  readonly plan: PlanKey;
  readonly cadence: BillingCadence;
}

export interface EffectivePlan {
  readonly plan: PlanKey;
  readonly access: AccessState;
  readonly providerStatus?: string;
}

export interface WorkspacePlanState {
  readonly catalogVersion: string;
  readonly desired: PlanSelection;
  readonly effective: EffectivePlan;
}

export function recordDesiredPlan(
  state: WorkspacePlanState,
  desired: PlanSelection,
): WorkspacePlanState {
  return { ...state, desired: { ...desired } };
}

export type TrustedPlanTransitionSource = "doorway-operator" | "billing-provider";

export function activateEffectivePlan(input: {
  state: WorkspacePlanState;
  plan: PlanKey;
  access: AccessState;
  source: TrustedPlanTransitionSource;
  providerStatus?: string;
}): WorkspacePlanState {
  return {
    ...input.state,
    effective: {
      plan: input.plan,
      access: input.access,
      ...(input.providerStatus === undefined ? {} : { providerStatus: input.providerStatus }),
    },
  };
}

export type MeteredEntitlement =
  | "liveApps"
  | "creatorSeats"
  | "doorwaySubdomains"
  | "customDomains"
  | "maxAppSizeBytes"
  | "monthlyAppVisits"
  | "monthlyAiRuns";

export type EntitlementDecision =
  | { ok: true; reason: "ALLOWED"; limit: number; current: number; requested: number }
  | {
    ok: false;
    reason:
      | "UNKNOWN_CATALOG_VERSION"
      | "UNKNOWN_PLAN"
      | "VALIDATION_FAILED"
      | "ENTITLEMENT_LIMIT_REACHED";
    limit?: number;
    current?: number;
    requested?: number;
  };

export function evaluateEntitlement(input: {
  state: WorkspacePlanState;
  capability: MeteredEntitlement;
  current: number;
  requested: number;
}): EntitlementDecision {
  if (
    !Number.isSafeInteger(input.current)
    || input.current < 0
    || !Number.isSafeInteger(input.requested)
    || input.requested <= 0
  ) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const definition = getPlanDefinition(input.state.catalogVersion, input.state.effective.plan);
  if (!definition.ok) return definition;
  const limit = definition.value.entitlements[input.capability];
  if (input.current + input.requested > limit) {
    return {
      ok: false,
      reason: "ENTITLEMENT_LIMIT_REACHED",
      limit,
      current: input.current,
      requested: input.requested,
    };
  }
  return {
    ok: true,
    reason: "ALLOWED",
    limit,
    current: input.current,
    requested: input.requested,
  };
}

export type FeatureEntitlement =
  | "passwordProtection"
  | "privateWorkspaceAccess"
  | "brandingRemoval"
  | "sharedOwnership"
  | "rolesAndOffboarding"
  | "workspaceProviderKeys"
  | "auditHistory"
  | "teamSkills";

export type FeatureEntitlementDecision =
  | { ok: true; reason: "ALLOWED" }
  | {
    ok: false;
    reason:
      | "UNKNOWN_CATALOG_VERSION"
      | "UNKNOWN_PLAN"
      | "ACCESS_STATE_RESTRICTED"
      | "ENTITLEMENT_NOT_INCLUDED";
  };

export function evaluateFeatureEntitlement(input: {
  state: WorkspacePlanState;
  capability: FeatureEntitlement;
}): FeatureEntitlementDecision {
  const definition = getPlanDefinition(input.state.catalogVersion, input.state.effective.plan);
  if (!definition.ok) return definition;
  if (input.capability === "teamSkills" && input.state.effective.access !== "active") {
    return { ok: false, reason: "ACCESS_STATE_RESTRICTED" };
  }
  return includesFeature(definition.value.entitlements, input.capability)
    ? { ok: true, reason: "ALLOWED" }
    : { ok: false, reason: "ENTITLEMENT_NOT_INCLUDED" };
}

function includesFeature(entitlements: PlanEntitlements, capability: FeatureEntitlement): boolean {
  switch (capability) {
    case "passwordProtection":
      return entitlements.visibility !== "public";
    case "privateWorkspaceAccess":
      return entitlements.visibility === "workspace-private-default";
    case "brandingRemoval":
      return entitlements.branding === "removable";
    case "sharedOwnership":
      return entitlements.ownership === "shared-workspace";
    case "rolesAndOffboarding":
      return entitlements.rolesAndOffboarding;
    case "workspaceProviderKeys":
      return entitlements.providerKeyOwner === "workspace";
    case "auditHistory":
      return entitlements.auditHistory;
    case "teamSkills":
      return entitlements.teamSkills;
  }
}

export type TeamSkillPermission = Extract<WorkspacePermission, `skill:${string}`>;

export type TeamSkillAccessDecision =
  | { ok: true; reason: "ALLOWED" }
  | {
      ok: false;
      reason:
        | "UNKNOWN_CATALOG_VERSION"
        | "UNKNOWN_PLAN"
        | "ENTITLEMENT_NOT_INCLUDED"
        | "ACCESS_STATE_RESTRICTED"
        | "MEMBERSHIP_INACTIVE"
        | "ROLE_PERMISSION_DENIED";
    };

export function evaluateTeamSkillAccess(input: {
  state: WorkspacePlanState;
  role: WorkspaceRole;
  memberStatus: MemberStatus;
  permission: TeamSkillPermission;
}): TeamSkillAccessDecision {
  if (input.memberStatus !== "active") {
    return { ok: false, reason: "MEMBERSHIP_INACTIVE" };
  }
  const entitlement = evaluateFeatureEntitlement({ state: input.state, capability: "teamSkills" });
  if (!entitlement.ok) return entitlement;
  return evaluatePermission({
    role: input.role,
    permission: input.permission,
    access: input.state.effective.access,
  });
}

export function parsePlanEntitlementsSnapshot(
  input: unknown,
): DomainResult<PlanEntitlements, "VALIDATION_FAILED"> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const value = input as Record<string, unknown>;
  const numericKeys = [
    "liveApps", "creatorSeats", "doorwaySubdomains", "customDomains", "maxAppSizeBytes",
    "monthlyAppVisits", "monthlyAiRuns", "revisionHistoryDays", "usageHistoryDays",
  ] as const;
  if (numericKeys.some((key) => !Number.isSafeInteger(value[key]) || (value[key] as number) < 0)) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const presentCapabilityKeys = WORKSPACE_CAPABILITY_KEYS.filter((key) => value[key] !== undefined);
  const isLegacyCapabilityShape = presentCapabilityKeys.length === 0
    || (presentCapabilityKeys.length === 1 && presentCapabilityKeys[0] === "teamSkills");
  if (
    (!isLegacyCapabilityShape && presentCapabilityKeys.length !== WORKSPACE_CAPABILITY_KEYS.length)
    || presentCapabilityKeys.some((key) => typeof value[key] !== "boolean")
  ) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  if (
    !isOneOf(value.appViewers, ["public", "public-or-password", "approved-unlimited"])
    || !isOneOf(value.providerKeyOwner, ["creator", "workspace"])
    || !isOneOf(value.visibility, ["public", "public-or-password", "workspace-private-default"])
    || !isOneOf(value.branding, ["required", "removable"])
    || !isOneOf(value.ownership, ["personal", "shared-workspace"])
    || typeof value.rolesAndOffboarding !== "boolean"
    || !isOneOf(value.identity, ["creator-account", "google-workspace-and-membership"])
    || typeof value.auditHistory !== "boolean"
    || !isOneOf(value.support, ["self-serve", "email", "priority-email"])
  ) {
    return { ok: false, reason: "VALIDATION_FAILED" };
  }
  const legacyPaidCapabilities = (value.customDomains as number) > 0;
  return {
    ok: true,
    value: {
      liveApps: value.liveApps as number,
      creatorSeats: value.creatorSeats as number,
      appViewers: value.appViewers,
      doorwaySubdomains: value.doorwaySubdomains as number,
      customDomains: value.customDomains as number,
      sourceBuilds: (value.sourceBuilds as boolean | undefined) ?? legacyPaidCapabilities,
      sourceSync: (value.sourceSync as boolean | undefined) ?? legacyPaidCapabilities,
      managedRuntimes: (value.managedRuntimes as boolean | undefined) ?? legacyPaidCapabilities,
      customApis: (value.customApis as boolean | undefined) ?? legacyPaidCapabilities,
      publicCredentialApps: (value.publicCredentialApps as boolean | undefined) ?? legacyPaidCapabilities,
      managedAi: (value.managedAi as boolean | undefined) ?? (value.monthlyAiRuns as number) > 0,
      agentPublishing: (value.agentPublishing as boolean | undefined) ?? legacyPaidCapabilities,
      teamSkills: value.teamSkills === true,
      hostedCollections: (value.hostedCollections as boolean | undefined) ?? true,
      maxAppSizeBytes: value.maxAppSizeBytes as number,
      monthlyAppVisits: value.monthlyAppVisits as number,
      monthlyAiRuns: value.monthlyAiRuns as number,
      providerKeyOwner: value.providerKeyOwner,
      visibility: value.visibility,
      branding: value.branding,
      revisionHistoryDays: value.revisionHistoryDays as number,
      usageHistoryDays: value.usageHistoryDays as number,
      ownership: value.ownership,
      rolesAndOffboarding: value.rolesAndOffboarding,
      identity: value.identity,
      auditHistory: value.auditHistory,
      support: value.support,
    },
  };
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === "string" && allowed.includes(value as T);
}

export type BillingHandoffStatus =
  | "provider-unavailable"
  | "ready"
  | "pending"
  | "completed"
  | "failed";

export interface BillingHandoff {
  readonly id: string;
  readonly workspaceId: string;
  readonly desired: PlanSelection;
  readonly status: BillingHandoffStatus;
  readonly createdAt: string;
  readonly provider?: string;
  readonly providerReference?: string;
}

export type BillingHandoffResult = DomainResult<
  BillingHandoff,
  "BILLING_PROVIDER_UNAVAILABLE" | "VALIDATION_FAILED" | "CONFLICT"
>;

export type InvitationStatus = "draft" | "pending" | "accepted" | "revoked" | "expired";

export const PROPOSED_MEMBER_ROLES = ["creator", "viewer"] as const;
export type ProposedMemberRole = (typeof PROPOSED_MEMBER_ROLES)[number];

export interface MemberDraft {
  readonly email: string;
  readonly role: ProposedMemberRole;
}

export interface TeamDraft {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly cadence: BillingCadence;
  readonly members: readonly MemberDraft[];
  readonly updatedAt: string;
}

export interface Invitation {
  readonly id: string;
  readonly workspaceId: string;
  readonly normalizedEmail: string;
  readonly role: ProposedMemberRole;
  readonly status: InvitationStatus;
  readonly expiresAt: string;
  readonly acceptedByUserId?: string;
}

export const AUDIT_ACTIONS = Object.freeze({
  workspaceBootstrap: "workspace.bootstrap",
  workspaceUpdated: "workspace.updated",
  membershipCreated: "membership.created",
  membershipRoleChanged: "membership.role-changed",
  membershipRevoked: "membership.revoked",
  projectVisibilityChanged: "project.visibility-changed",
  projectPublished: "project.published",
  projectDeleted: "project.deleted",
  secretChanged: "secret.changed",
  customDomainChanged: "custom-domain.changed",
  publicApiPolicyChanged: "public-api.policy-changed",
  appDataRestored: "app-data.restored",
  entitlementChanged: "entitlement.changed",
  billingStateChanged: "billing-state.changed",
  billingPlanIntentRecorded: "billing.plan-intent-recorded",
  teamDraftSaved: "team-draft.saved",
} as const);

export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS];

export interface AuditEvent {
  readonly id: string;
  readonly actorId: string;
  readonly workspaceId: string;
  readonly action: AuditAction;
  readonly resourceType: string;
  readonly resourceId: string;
  readonly occurredAt: string;
  readonly metadata: Readonly<Record<string, string | number | boolean | null>>;
}

export type UsageMetric =
  | "liveApps"
  | "creatorSeats"
  | "doorwaySubdomains"
  | "customDomains"
  | "appSizeBytes"
  | "appVisits"
  | "publishes"
  | "aiRuns";

export type UsageLimitState = "available" | "warning" | "exhausted" | "not-included";

export interface UsageLine {
  readonly metric: UsageMetric;
  readonly used: number;
  readonly limit: number;
  readonly remaining: number;
  readonly state: UsageLimitState;
}

export interface ProjectUsageBreakdown {
  readonly projectId: string;
  readonly metrics: readonly UsageLine[];
}

export interface WorkspaceUsageSummary {
  readonly workspaceId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly metrics: readonly UsageLine[];
  readonly projects: readonly ProjectUsageBreakdown[];
}

export interface EffectiveEntitlementSnapshot {
  readonly workspaceId: string;
  readonly catalogVersion: string;
  readonly effectivePlan: PlanKey;
  readonly access: AccessState;
  readonly entitlements: PlanEntitlements;
  readonly capturedAt: string;
}
